import createAPIManager from './manager/apiManager';
import createEntityManager from './manager/entityManager';
import { type Configuration } from './model/configuration';
import { type Dependencies } from './model/dependencies';

/**
 * Creates and returns an Orion connector instance.
 *
 * The instance can be used to retrieve Orion settings and segmentations and subscribe to their changes.
 * @param config - Configuration object for the Orion connector.
 * @param [dependencies] - Optional dependencies for testing purposes.
 */
export async function createOrion<Settings extends Record<string, unknown>>(
  config: Configuration<Settings>,
  dependencies: Dependencies = {},
) {
  const apiManagerBuilder = dependencies.createAPIManager || createAPIManager;
  const entityManagerBuilder = dependencies.createEntityManager || createEntityManager;

  const apiManager = apiManagerBuilder({
    fetcher: config.fetcher,
    appId: config.appId,
    baseUrl: config.baseUrl,
    // The default of goesThroughOrionProxy is false
    goesThroughOrionProxy: config.goesThroughOrionProxy ?? false,
  });

  const entityManager = await entityManagerBuilder<Settings>({
    apiManager: apiManager,
    persistenceManager: config.persistenceManager,
    logger: config.logger,
    defaultSettings: config.defaultSettings,
    defaultSegmentations: config.defaultSegmentations,
    deviceAttributeGetters: config.deviceAttributeGetters,
    appSpecificAttributeGetters: config.appSpecificAttributeGetters,
    userIdGenerator: config.userIdGenerator,
    shouldReturnDefaultsImmediately: config.shouldReturnDefaultsImmediately,
    identityCacheTtlInMs: config.identityCacheTtlInMs,
  });

  return {
    identity: {
      /**
       * Get the current Orion identity.
       */
      get: entityManager.getIdentity,
    },
    /**
     * Entrypoint to access Orion secret menu features.
     */
    secret: {
      /**
       * Returns whether the current user has access to the secret menu API.
       */
      isAvailable: entityManager.isSecretMenuEligible,
      /**
       * Get all Orion experiments
       */
      getAllExperiments: entityManager.getAllExperiments,
      /**
       * Set the user inside a specific experiment segment
       */
      setExperimentSegmentation: entityManager.setExperimentSegmentation,
      /**
       * Set the user inside specific segments in the given experiments
       */
      setExperimentSegmentations: entityManager.setExperimentSegmentations,
      /**
       * Get the current device attributes
       */
      getDeviceAttributes: entityManager.getDeviceAttributes,
      /**
       * Get the current user attributes
       */
      getUserAttributes: entityManager.getUserAttributes,
      /**
       * Set the segmentation state for the user.
       * If exclude_from_segmentation=True, remove the identity from ALL experiments and stop it from being segmented into any other experiments until included in segmentation again.
       * If exclude_from_segmentation=False, make the identity segmental again.
       */
      excludeFromSegmentation: entityManager.excludeFromSegmentation,
    },
  };
}
